'use client';

import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import { Marquee } from 'components/shared/marquee';
import { SCIENCE_PARTNER_LOGOS } from 'components/science/home/scienceLogoArt';

import { useReveal } from './useReveal';
import styles from './PartnersStrip.module.css';

/* Partners strip — infinite logo marquee at all breakpoints, opacity 0.7,
   pause on hover/focus and reduced motion. */

const REPEAT = 3;

export default function PartnersStrip() {
  const t = useTranslations('science');
  const [play, setPlay] = useState(true);
  // heading + logo row fade up once (left as-is when already on screen at load)
  const revealRef = useReveal<HTMLElement>();

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPlay(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return (
    <section
      ref={revealRef}
      className={styles.strip}
      aria-labelledby="science-partners-title"
      onFocusCapture={() => setPlay(false)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setPlay(!window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        }
      }}
    >
      <h2 id="science-partners-title" className={styles.heading} data-reveal>
        {t('science.landing.partners.eyebrow')}
      </h2>
      <div className={styles.masked} data-reveal>
        <Marquee gradient={false} speed={24} play={play} pauseOnHover>
          <div className={styles.row} aria-hidden>
            {Array.from({ length: REPEAT }).flatMap((_, r) =>
              SCIENCE_PARTNER_LOGOS.map((logo) => (
                <span
                  key={`${logo.id}-${r}`}
                  className={styles.mark}
                  style={{ '--lh': `${logo.height}px` } as CSSProperties}
                >
                  <img
                    src={logo.src}
                    alt={r === 0 ? logo.alt : ''}
                    width={logo.width}
                    height={logo.height}
                    draggable={false}
                  />
                </span>
              )),
            )}
          </div>
        </Marquee>
      </div>
    </section>
  );
}
